<template>
  <div>
    <div class="d-flex justify-end mb-4">
      <v-text-field
        id="log-unitizacao-consultas-search"
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 250px;"
      />
    </div>

    <general-progress-bars
      class="mb-4"
      :items="downloadProgressData"
      prefix-text="Baixando arquivo da consulta"
    />

    <v-data-table
      class="log-unitizacao-consultas-table"
      :headers="headers"
      :items="itemsFormatados"
      :search.sync="search"
      :header-props="{ sortByText: 'Ordenar Por' }"
      :footer-props="{ itemsPerPageText: 'Itens por Página' }"
      no-data-text="Nenhuma consulta de Log de Unitização disponível"
      no-results-text="Nenhum registro correspondente encontrado"
      :loading="loading"
      mobile-breakpoint="800"
    >
      <template v-slot:[`item.status`]="{ item }">
        <general-status :status="item.status" />
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          id="action-download-consulta-log-unitizacao"
          :disabled="!canDownload(item)"
          :class="!canDownload(item) ? 'cursor-block' : ''"
          min-width="0"
          class="px-1"
          fab
          icon
          x-small
          :loading="downloadProgressData[item.id] !== undefined"
          @click="downloadItem(item)"
        >
          <v-icon small>
            mdi-download
          </v-icon>
        </v-btn>

        <v-btn
          id="action-details-consulta-log-unitizacao"
          color="white"
          class="ml-1"
          height="22px"
          width="22px"
          fab
          x-small
          elevation="1"
          @click="openDialogDetalhes(item)"
        >
          <v-icon small>
            mdi-file-find-outline
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:[`footer`]>
        <v-switch
          v-if="$store.getters.userIsAdmin"
          class="pa-0 pl-2 swithInterna"
          style="position: absolute;"
          color="primary"
          label="Exibir Expirados"
          dense
          @change="$emit('switchProcessamentosExpirados', $event)"
        />
      </template>

      <template v-slot:[`footer.page-text`]>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="$emit('rechargeTable')"
            >
              mdi-refresh
            </v-icon>
          </template>
          <span>Clique aqui para recarregar o histórico</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <general-dialog-detalhes
      title="Detalhes da Consulta de Log de Unitização"
      :show-dialog="dialogDetalhes"
      max-width="1000px"
      @changeValue="dialogDetalhes = $event"
    >
      <template slot="body">
        <v-progress-linear
          v-if="dialogDetalhesLoading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <v-data-table
          :headers="resumoHeaders"
          :items="dialogResumoData"
          hide-default-footer
          class="elevation-1 style-table-dialog-detalhes"
          disable-pagination
          mobile-breakpoint="10"
        />

        <v-alert
          v-if="dialogDetalhesItem && dialogDetalhesItem.status !== 'SUCESSO'"
          dense
          outlined
          type="info"
          class="mt-4 mb-0"
        >
          O arquivo da consulta só fica disponível para download quando o processamento termina com sucesso.
        </v-alert>
      </template>
    </general-dialog-detalhes>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import LogUnitizacaoService from '@/services/LogUnitizacaoService';

export default {
  name: 'LogUnitizacaoTabelaConsultas',

  components: {
    GeneralProgressBars: () => import('@/components/general/GeneralProgressBars.vue'),
    GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
    GeneralDialogDetalhes: () => import('@/components/general/GeneralDialogDetalhes')
  },

  props: {
    items: {
      type: Array,
      default: () => []
    },
    uploads: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      search: '',
      downloadProgressData: {},
      dialogDetalhes: false,
      dialogDetalhesLoading: false,
      dialogDetalhesItem: null,
      dialogResumoData: [],
      headers: [
        { text: '#', value: 'id' },
        { text: 'Usuario', value: 'usuario_nome' },
        { text: 'Processado em', value: 'processado_em_formatado' },
        { text: 'Status', value: 'status' },
        { text: 'Qtd. obras', value: 'qtd_codigos_obra' },
        { text: 'Qtd. chaves', value: 'qtd_chaves' },
        { text: 'Ações', value: 'actions', sortable: false, class: 'pl-4' }
      ],
      resumoHeaders: [
        { text: 'Resumo', value: 'parametro', sortable: false },
        { text: 'Valor', value: 'valor', sortable: false }
      ]
    };
  },

  computed: {
    itemsFormatados() {
      return this.items.map((item) => ({
        ...item,
        usuario_nome: item.usuario ? item.usuario.nome : '-',
        processado_em_formatado: item.processado_em ? dayjs(item.processado_em).format('DD/MM/YYYY HH:mm') : '-'
      }));
    }
  },

  methods: {
    canDownload(item) {
      return item.status === 'SUCESSO';
    },

    async downloadItem(item) {
      if (!this.canDownload(item)) {
        return;
      }

      const id = item.id;
      this.$set(this.downloadProgressData, id, { id });
      this.$toast.info('Preparando o arquivo da consulta para download.', '', { position: 'topRight', timeout: 2000 });

      try {
        const response = await LogUnitizacaoService.downloadConsulta(id, {
          timeout: 60 * 60 * 1000,
          onDownloadProgress: (progressEvent) => this.updateDownloadProgress(id, progressEvent)
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', `consulta_log_unitizacao_${item.id}.xlsx`);
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error(this.getApiErrorMessage(error, 'Falha no download da consulta.'), '', { position: 'topRight' });
        console.error(error);
      } finally {
        this.$delete(this.downloadProgressData, id);
      }
    },

    updateDownloadProgress(id, progressEvent) {
      const progress = progressEvent.total
        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
        : 0;

      this.$set(this.downloadProgressData, id, {
        id,
        progress,
        isComputable: progressEvent.lengthComputable
      });
    },

    async openDialogDetalhes(item) {
      this.dialogDetalhes = true;
      this.dialogDetalhesLoading = true;
      this.dialogDetalhesItem = item;
      this.dialogResumoData = this.buildResumo(item);

      try {
        const response = await LogUnitizacaoService.getConsulta(item.id);
        const consulta = response.data.data;

        this.dialogDetalhesItem = consulta;
        this.dialogResumoData = this.buildResumo(consulta);
      } catch (error) {
        this.$toast.error(this.getApiErrorMessage(error, 'Erro ao carregar detalhes da consulta.'), '', { position: 'topRight' });
        console.error(error);
      } finally {
        this.dialogDetalhesLoading = false;
      }
    },

    buildResumo(consulta) {
      return [
        { parametro: 'ID', valor: consulta.id || '-' },
        { parametro: 'USUARIO', valor: consulta.usuario ? consulta.usuario.nome : (consulta.usuario_nome || '-') },
        { parametro: 'STATUS', valor: consulta.status || '-' },
        { parametro: 'PROCESSADO EM', valor: consulta.processado_em ? dayjs(consulta.processado_em).format('DD/MM/YYYY HH:mm') : '-' },
        { parametro: 'QTD. OBRAS', valor: consulta.qtd_codigos_obra !== undefined ? consulta.qtd_codigos_obra : '-' },
        { parametro: 'QTD. CHAVES', valor: consulta.qtd_chaves !== undefined ? consulta.qtd_chaves : '-' },
        { parametro: 'CODIGOS DE OBRA', valor: this.formatCodigosObra(consulta) },
        { parametro: 'RESUMO', valor: consulta.resumo_processamento || '-' }
      ];
    },

    formatCodigosObra(consulta) {
      if (Array.isArray(consulta.codigos_obra) && consulta.codigos_obra.length) {
        return consulta.codigos_obra.join(', ');
      }

      if (consulta.codigo_obras) {
        return consulta.codigo_obras.split(',').map((codigo) => codigo.trim()).filter(Boolean).join(', ');
      }

      return '-';
    },

    getApiErrorMessage(error, fallbackMessage) {
      if (error && error.response && error.response.data && error.response.data.erro) {
        return error.response.data.erro;
      }

      if (error && error.data && error.data.erro) {
        return error.data.erro;
      }

      return fallbackMessage;
    }
  }
};
</script>

<style>
@media screen and (min-width: 800px) {
  .log-unitizacao-consultas-table table tr td:nth-child(8) {
    white-space: nowrap !important;
  }
}

.swithInterna label {
  font-size: 13px !important;
  margin-left: 0 !important;
}
</style>
